import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from "react";

interface WebAgentContextType {
  isOpen: boolean;
  setIsOpen: (open: boolean) => void;
  openerRef: React.MutableRefObject<HTMLElement | null>;
}

const WebAgentContext = createContext<WebAgentContextType | undefined>(undefined);

export const AGENT_OPEN_STORAGE_KEY = "agentChatOpen";

export const WebAgentProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isOpen, setIsOpenState] = useState(false);
  const openerRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    const saved = sessionStorage.getItem(AGENT_OPEN_STORAGE_KEY);
    if (saved === "true") {
      setIsOpenState(true);
    }
  }, []);

  useEffect(() => {
    sessionStorage.setItem(AGENT_OPEN_STORAGE_KEY, isOpen.toString());
  }, [isOpen]);

  const setIsOpen = useCallback((open: boolean) => {
    if (open && !openerRef.current) {
      openerRef.current = document.activeElement as HTMLElement | null;
    }
    setIsOpenState(open);
  }, []);

  return (
    <WebAgentContext.Provider value={{ isOpen, setIsOpen, openerRef }}>
      {children}
    </WebAgentContext.Provider>
  );
};

export const useWebAgentContext = (): WebAgentContextType => {
  const context = useContext(WebAgentContext);
  if (!context) {
    throw new Error("useWebAgentContext must be used within a WebAgentProvider");
  }
  return context;
};
